import { createContext } from 'react'
import type {
  LoyaltyMember,
  LoyaltyMemberLogsResponse,
  LoyaltyMemberSortBy,
  LoyaltyMemberStatus,
  LoyaltyMembersParams,
  LoyaltyMembersResponse,
  LoyaltySortDirection,
  LoyaltyTier,
} from '../../../types/loyalty'

export type LoyaltyTierFilter = 'all' | LoyaltyTier
export type LoyaltyStatusFilter = 'all' | LoyaltyMemberStatus

export interface MembersContextValue {
  storeId: string

  // ── Server data ─────────────────────────────────────────────────────────────
  pageData: LoyaltyMembersResponse | undefined
  members: LoyaltyMember[]
  membersLoading: boolean
  membersFetching: boolean

  // ── Search (debounced → forwarded as `search` param) ────────────────────────
  keyword: string
  setKeyword: (value: string) => void

  // ── Server-side filters ─────────────────────────────────────────────────────
  tierFilter: LoyaltyTierFilter
  setTierFilter: (tier: LoyaltyTierFilter) => void
  statusFilter: LoyaltyStatusFilter
  setStatusFilter: (status: LoyaltyStatusFilter) => void

  // ── Sort ────────────────────────────────────────────────────────────────────
  sortBy: LoyaltyMemberSortBy
  setSortBy: (sortBy: LoyaltyMemberSortBy) => void
  sortDirection: LoyaltySortDirection
  setSortDirection: (dir: LoyaltySortDirection) => void

  // ── Pagination ──────────────────────────────────────────────────────────────
  listParams: LoyaltyMembersParams
  currentPage: number
  totalPages: number
  totalItems: number
  canGoBack: boolean
  canGoNext: boolean
  showingStart: number
  showingEnd: number
  goToPrevPage: () => void
  goToNextPage: () => void
  goToPage: (page: number) => void

  // ── Drawer (per-member detail view) ─────────────────────────────────────────
  // The drawer renders summary cards from the row data plus, when the API
  // returns `customer_id`, a points-history feed loaded from
  // useLoyaltyMemberLogsQuery.
  drawerOpen: boolean
  selectedMember: LoyaltyMember | null
  openMember: (member: LoyaltyMember) => void
  closeMember: () => void
  memberDetail: LoyaltyMemberLogsResponse | undefined
  memberDetailLoading: boolean

  // ── Adjust Points modal ─────────────────────────────────────────────────────
  // Owned here so the trigger (drawer button) and the renderer (mounted on the
  // tab body) can share state without prop-drilling. `adjustTargetMember` is
  // independent of `selectedMember` to support future entry points (e.g. row
  // kebab menu) without forcing the drawer open.
  adjustModalOpen: boolean
  adjustTargetMember: LoyaltyMember | null
  openAdjustPoints: (member: LoyaltyMember) => void
  closeAdjustPoints: () => void
}

export const MembersContext = createContext<MembersContextValue | undefined>(
  undefined,
)
